import { useRef, useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Avatar, Btn, Textarea, RoleBadge } from "@/forum/ui";

const COLORS = ["#5eb1ff", "#ff5e8a", "#7cffb2", "#ffc85e", "#b48cff", "#5effe6", "#ff9b5e", "#ffffff"];

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const [bio, setBio] = useState(user?.bio || "");
  const [color, setColor] = useState(user?.avatar_color || COLORS[0]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/forum/me", { bio, avatar_color: color });
      await refresh();
      toast.success("Profil mentve");
    } catch (ex) { toast.error(errorText(ex)); } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) return toast.error("Csak JPG, PNG, WEBP vagy GIF kép tölthető fel");
    if (file.size > 2 * 1024 * 1024) return toast.error("A kép legfeljebb 2 MB lehet");
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/forum/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Profilkép frissítve");
    } catch (ex) {
      toast.error(errorText(ex));
      setPreview(null);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    setUploading(true);
    try {
      await api.delete("/forum/me/avatar");
      setPreview(null);
      await refresh();
      toast.success("Profilkép eltávolítva");
    } catch (ex) { toast.error(errorText(ex)); } finally { setUploading(false); }
  };

  if (!user) return null;
  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="settings-page">
      <div>
        <p className="eyebrow mb-3">Fiók</p>
        <h1 className="font-display text-3xl font-bold">Beállítások</h1>
      </div>
      <Panel className="p-6 sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <div className="relative">
            <Avatar user={{ ...user, avatar_color: color }} size="xl" src={preview} />
            {uploading && <span className="absolute inset-0 grid place-items-center rounded-full bg-black/50"><span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" /></span>}
          </div>
          <div className="flex-1">
            <p className="font-display text-lg font-bold">{user.username}</p>
            <p className="font-mono text-xs text-white/40">{user.email}</p>
            <RoleBadge role={user.role} label={user.role_label} className="mt-2" />
            <div className="mt-4 flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="avatar-file-input" />
              <Btn type="button" variant="ghost" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="avatar-upload-button"><Upload size={14} />Profilkép feltöltése</Btn>
              {(user.avatar_url || preview) && <Btn type="button" variant="danger" onClick={removeAvatar} disabled={uploading} data-testid="avatar-remove-button"><Trash2 size={14} />Eltávolítás</Btn>}
            </div>
            <p className="mt-2 font-mono text-[10px] text-white/35">JPG, PNG, WEBP vagy GIF · max. 2 MB · négyzetes kép ajánlott</p>
          </div>
        </div>
        <form onSubmit={save} className="mt-8 space-y-6">
          <div>
            <label className="eyebrow mb-3 block">Monogram színe <span className="normal-case tracking-normal text-white/30">(ha nincs profilkép)</span></label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)} style={{ background: c }} className={`h-9 w-9 rounded-full transition-transform hover:scale-110 ${color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#111113]" : ""}`} data-testid={`color-${c.slice(1)}`} aria-label={c} />
              ))}
            </div>
          </div>
          <div>
            <label className="eyebrow mb-3 block">Bemutatkozás</label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} maxLength={500} placeholder="Pár szó rólad / a karakteredről…" data-testid="settings-bio" />
            <p className="mt-1 text-right font-mono text-[10px] text-white/30">{bio.length}/500</p>
          </div>
          <div className="flex justify-end"><Btn type="submit" disabled={busy} data-testid="settings-save">Mentés</Btn></div>
        </form>
      </Panel>
    </div>
  );
}
